import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { integrateCloverRequest } from '../redux/slices/cloverSlice';
import { useQueryParams } from './useQueryParams';
import { useSSETaskMonitor } from './useSSETaskMonitor';
import { useMenuPullCache } from './useMenuPullCache';
import { ENDPOINTS } from '../api/endPoints';
import type { SSETaskStatus } from './useSSETaskMonitor';

export interface UseCloverIntegrationReturn {
  loading: boolean;
  showMenuLoader: boolean;
  status: SSETaskStatus;
  isMonitoring: boolean;
  cloverError: string | null;
  taskError: string | null;
  hasError: boolean;
}

export const useCloverIntegration = (): UseCloverIntegrationReturn => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Parse OAuth query parameters
  const { merchantId, employeeId, clientId, code } = useQueryParams();
  
  // Get Clover integration state from Redux
  const { success, restaurantId, loading, error: cloverError } = useAppSelector((state) => state.clover);
  
  // Prevent duplicate integration dispatch
  const hasTriggeredIntegrationRef = useRef(false);
  
  // Menu pull cache management with restaurant ID validation
  const { markMenuAsPulled, isValidCache } = useMenuPullCache({
    restaurantId,
  });
  
  // Build SSE monitor URL dynamically using cloverOauth endpoint
  const sseMonitorUrl = useMemo(() => {
    if (!restaurantId) return null;
    // Using the cloverOauth endpoint base with task monitoring params
    const baseUrl = ENDPOINTS.cloverMoniterUrl.split('?')[0]; // Get base URL without query params
    return `${baseUrl}?type=appmarket&taskId=${restaurantId}`;
  }, [restaurantId]);
  
  // Handle SSE completion
  const handleSSEComplete = useCallback(() => {
    markMenuAsPulled();
  }, [markMenuAsPulled]);
  
  // SSE Task Monitoring
  // Only skip monitoring if cache is valid for THIS restaurant
  const { status, isMonitoring, message: taskError } = useSSETaskMonitor({
    monitorUrl: sseMonitorUrl,
    enabled: success && !!restaurantId && !isValidCache,
    onCompleted: handleSSEComplete,
  });
  
  // Handle Clover integration when route is /integrate-clover
  useEffect(() => {
    if (merchantId) {
      // If all parameters are present and not already triggered, dispatch integration
      if (
        merchantId && 
        employeeId && 
        clientId && 
        code && 
        !hasTriggeredIntegrationRef.current
      ) {
        hasTriggeredIntegrationRef.current = true;
        dispatch(
          integrateCloverRequest({
            merchant_id: merchantId,
            employee_id: employeeId,
            client_id: clientId,
            code,
          })
        );
      } else if (!merchantId || !employeeId || !clientId || !code) {
        // If parameters are missing, redirect to home
        navigate('/');
      }
    }
  }, [location.pathname, merchantId, employeeId, clientId, code, dispatch, navigate]);
  
  // Calculate loader visibility
  // Show loader if cache is invalid AND (monitoring is active OR status indicates processing/completed)
  // Include 'completed' to allow MenuPullingLoader to show "Menu Ready!" screen
  const showMenuLoader = !isValidCache && (
    isMonitoring || 
    status === 'processing' || 
    status === 'extracting'
  );
  
  // Determine if there's any error
  const hasError = !!cloverError || status === 'failed';
  
  return {
    loading,
    showMenuLoader,
    status,
    isMonitoring,
    cloverError,
    taskError: status === 'failed' ? (taskError || 'Task failed') : null,
    hasError,
  };
};
